import { useAccounts } from "@/hooks/api";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { apiClient } from "@/services/api";
import type { FoldMoneyAvailableAccount } from "@/types";
import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableColumnHeaderClass,
} from "@groot/ui/table";
import { Check, Copy, Loader2, Plug } from "lucide-react";
import { useState } from "react";

const headerCellClass = cn(tableColumnHeaderClass, "px-3 py-2 text-left whitespace-nowrap");

const PHONE_STORAGE_KEY = "nesins.integrations.foldPhone";

const TYPE_LABELS: Record<string, string> = {
  bank: "Bank",
  credit_card: "Credit Card",
  investment: "Investment",
  other: "Other",
};

function CopyIdButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({ title: "Copied", description: value });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({
        title: "Copy failed",
        description: "Couldn't access the clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      onClick={handleCopy}
      aria-label="Copy Fold Money account ID"
      className="ml-1.5 align-middle"
    >
      {copied ? (
        <Check className="size-3.5 text-success" />
      ) : (
        <Copy className="size-3.5 text-muted-foreground" />
      )}
    </Button>
  );
}

/**
 * "Integrations" settings tab — live discovery of the bank accounts linked in
 * your Fold Money profile. The Fold-side `id` is what goes into an account's
 * `bankSyncConfig.accountId` when configuring a new bank for sync.
 *
 * Reuses the stored sync tokens server-side; if they're stale the backend
 * returns a 502 asking you to run a fold-money-sync job first.
 */
export function BankSyncIntegrations() {
  const [phone, setPhone] = useState(() => localStorage.getItem(PHONE_STORAGE_KEY) ?? "");
  const [accounts, setAccounts] = useState<FoldMoneyAvailableAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Local accounts let us label Fold IDs that are already linked with their
  // real bank names (Fold itself only reports generic "Bank Account <id8>").
  const { data: localAccounts = [] } = useAccounts();
  const localNameByFoldId = new Map<string, string>();
  for (const account of localAccounts) {
    const config = account.bankSyncConfig as { accountId?: unknown } | null | undefined;
    if (typeof config?.accountId === "string" && config.accountId.length > 0) {
      localNameByFoldId.set(config.accountId, account.accountName);
    }
  }

  const handleFetch = async () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      toast({
        title: "Phone required",
        description: "Enter the phone number your Fold Money sync uses",
        variant: "destructive",
      });
      return;
    }

    setIsFetching(true);
    setError(null);
    try {
      const result = await apiClient.getFoldMoneyAccounts(trimmed);
      localStorage.setItem(PHONE_STORAGE_KEY, trimmed);
      setAccounts(result);
    } catch (err) {
      setAccounts(null);
      setError(err instanceof Error ? err.message : "Failed to fetch Fold Money accounts");
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Bank accounts linked in your Fold Money profile. The Fold-side ID is what goes into an
        account's
        <code className="mx-1 font-mono text-[0.9em]">bankSyncConfig.accountId</code>
        when configuring a new bank for sync. Fold only reports generic names (&ldquo;Bank Account
        15cef067&rdquo;) — already-linked accounts are labeled with their local name below, and the
        raw JSON has everything Fold returns.
      </p>

      <div className="flex items-center gap-2 max-w-md">
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleFetch();
          }}
          placeholder="Fold Money phone number"
          aria-label="Fold Money phone number"
          inputMode="tel"
        />
        <Button onClick={handleFetch} disabled={isFetching} className="gap-1.5 shrink-0">
          {isFetching ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Plug className="size-3.5" />
          )}
          Fetch accounts
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {accounts !== null &&
        !error &&
        (accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No bank accounts linked in this Fold Money profile.
          </p>
        ) : (
          <div className="rounded-lg border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={headerCellClass}>Account name</TableHead>
                  <TableHead className={headerCellClass}>Type</TableHead>
                  <TableHead className={headerCellClass}>Linked local account</TableHead>
                  <TableHead className={headerCellClass}>Fold Money ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="px-3 py-2">
                      <span className="text-sm">{account.name}</span>
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <Badge variant="secondary">{TYPE_LABELS[account.type] ?? account.type}</Badge>
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      {localNameByFoldId.get(account.id) ? (
                        <span className="text-sm">{localNameByFoldId.get(account.id)}</span>
                      ) : (
                        <Badge variant="outline">Not linked</Badge>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <span className="inline-flex items-center">
                        <span className="font-mono text-xs">{account.id}</span>
                        <CopyIdButton value={account.id} />
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}

      {accounts !== null && accounts.length > 0 && (
        <details>
          <summary className="cursor-pointer select-none text-sm text-muted-foreground hover:text-foreground">
            Raw JSON
          </summary>
          <pre
            className={cn(
              "mt-2 rounded-lg border border-border bg-muted/30 p-3 overflow-auto max-h-96",
              "font-mono text-xs",
            )}
          >
            {JSON.stringify(accounts, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
